/**
 * Module summary:
 * This file coordinates sign-in, sign-out, registration, password reset,
 * verification emails, Google sign-in, and app-level auth subscriptions. It
 * wraps Firebase Auth in production mode and keeps a local fallback for
 * development mode so the rest of the app can use one consistent API.
 */

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  signInWithCustomToken as firebaseSignInWithCustomToken,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  getMultiFactorResolver,
  type MultiFactorError,
  type User as FirebaseUser,
  type ActionCodeSettings
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import {
  getFirebaseAuth,
  getFirebaseDb,
  initializeFirebaseAuth,
} from '../firebaseConfig';
import {
  INPUT_LIMITS,
  assert,
  isStrongPassword,
  isValidEmail,
  normalizeEmail,
  normalizeUsPhoneE164,
  sanitizeSingleLineText,
  toOptionalSingleLineText,
} from '../../utils/inputSecurity';

const USE_FIREBASE = (process.env.REACT_APP_DATA_PROVIDER || 'firebase') === 'firebase';

export interface User {
  id: string;
  email: string;
  displayName: string;
  organization?: string;
  phone?: string;
  address?: string;
  organizationAddress?: string;
  roles: ('admin' | 'owner' | 'user' | 'manager')[];
  assignedBuildings: string[];
}

export interface AuthSession {
  user: User;
  token: string;
  expiresAt: number;
}

let MOCK_USERS: Record<string, { password: string; user: User }> = {};

type AuthStateListener = (user: User | null) => void;
const listeners: AuthStateListener[] = [];

const loginAttempts = new Map<string, { count: number; lockedUntil?: number }>();
// These constants control the local safety rails that slow repeated login failures
// and define how long a trusted MFA device should stay valid.
const MAX_ATTEMPTS = Number(process.env.REACT_APP_MAX_LOGIN_ATTEMPTS || 5);
const LOCK_WINDOW_MS = 10 * 60 * 1000;
const MFA_TRUST_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const LEGACY_MFA_ENROLLED_KEY = 'macha.mfaEnrolled';

function notifyListeners(user: User | null) {
  // Notify every subscribed hook or component so the UI reacts immediately to auth changes.
  listeners.forEach(listener => listener(user));
}

function firebaseUserToUser(fbUser: FirebaseUser): User {
  return {
    // Map the Firebase user record into the richer app-level shape and fill
    // optional fields with safe defaults until Firestore profile data arrives.
    id: fbUser.uid,
    email: fbUser.email || '',
    displayName: fbUser.displayName || '',
    organization: '',
    phone: '',
    address: '',
    organizationAddress: '',
    roles: ['user'],
    assignedBuildings: ['building-001']
  };
}

/**
 * Fetch user roles from Firestore and return an enriched User object.
 */
async function enrichUserWithRoles(fbUser: FirebaseUser): Promise<User> {
  const base = firebaseUserToUser(fbUser);
  if (!USE_FIREBASE) return base;
  try {
    const db = getFirebaseDb();
    // Read the companion Firestore user document because roles, buildings, and
    // profile details live there rather than in Firebase Auth itself.
    const snap = await getDoc(doc(db, 'users', fbUser.uid));
    if (snap.exists()) {
      const data = snap.data();
      if (Array.isArray(data.roles) && data.roles.length > 0) {
        base.roles = data.roles;
      }
      if (Array.isArray(data.assignedBuildings) && data.assignedBuildings.length > 0) {
        base.assignedBuildings = data.assignedBuildings;
      }
      if (typeof data.organization === 'string' && data.organization.trim()) {
        base.organization = data.organization.trim();
      }
      if (typeof data.phone === 'string' && data.phone.trim()) {
        base.phone = data.phone.trim();
      }
      if (typeof data.organizationAddress === 'string' && data.organizationAddress.trim()) {
        base.organizationAddress = data.organizationAddress.trim();
      }
      if (typeof data.address === 'string' && data.address.trim()) {
        base.address = data.address.trim();
      }
    }
  } catch (err) {
    console.error('Failed to fetch user roles from Firestore:', err);
  }
  return base;
}

/**
 * Build ActionCodeSettings for email actions (verification, password reset).
 * These settings direct the user back to the app without relying on Firebase Dynamic Links.
 */
function getActionCodeSettings(): ActionCodeSettings {
  const authDomain = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN;
  // Prefer the configured auth domain so Firebase email links round-trip
  // through the expected host instead of a mismatched development origin.
  const url = authDomain ? `https://${authDomain}` : window.location.origin;
  return {
    url,
    handleCodeInApp: false,
  };
}

async function saveUserProfileToFirestore(fbUser: FirebaseUser, extra?: { phone?: string; organization?: string; address?: string; organizationAddress?: string }) {
  if (!USE_FIREBASE) return;
  try {
    const db = getFirebaseDb();
    const userRef = doc(db, 'users', fbUser.uid);
    const snap = await getDoc(userRef);
    const resolvedAddress = sanitizeSingleLineText(extra?.organizationAddress || extra?.address || '', INPUT_LIMITS.address);
    const normalizedPhone = extra?.phone ? normalizeUsPhoneE164(extra.phone) || '' : '';
    const normalizedOrganization = sanitizeSingleLineText(extra?.organization || '', INPUT_LIMITS.organization);
    if (!snap.exists()) {
      // Create the Firestore profile document the first time the account appears
      // so later role/profile reads have a stable schema to work with.
      await setDoc(userRef, {
        uid: fbUser.uid,
        email: fbUser.email || '',
        displayName: sanitizeSingleLineText(fbUser.displayName || '', INPUT_LIMITS.displayName),
        phone: normalizedPhone,
        organization: normalizedOrganization,
        organizationAddress: resolvedAddress,
        address: resolvedAddress,
        roles: ['user'],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } else {
      const patch: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
      };
      // Only patch fields that were actually supplied so existing profile data
      // is not overwritten by empty values.
      if (typeof extra?.phone === 'string') patch.phone = normalizedPhone;
      if (typeof extra?.organization === 'string') patch.organization = normalizedOrganization;
      if (typeof extra?.organizationAddress === 'string' || typeof extra?.address === 'string') {
        patch.organizationAddress = resolvedAddress;
        patch.address = resolvedAddress;
      }
      if (Object.keys(patch).length > 1) {
        await setDoc(userRef, patch, { merge: true });
      }
    }
  } catch (err) {
    console.error('Failed to save user profile to Firestore:', err);
  }
}

function setSession(user: User) {
  const session: AuthSession = {
    user,
    // Store a simple mock token so development mode still behaves like a real session flow.
    token: `mock-token-${Date.now()}`,
    expiresAt: Date.now() + 3600000
  };
  localStorage.setItem('mockAuthSession', JSON.stringify(session));
}

function getMfaTrustKey(userId: string): string {
  return `macha.mfaTrustedDevice.${userId}`;
}

function getMfaEnrollmentKey(userId: string): string {
  return `macha.mfaEnrolled.${userId}`;
}

function rememberTrustedMfaDevice(userId: string): void {
  const record = {
    // Set the expiry in advance so later checks only need to compare timestamps.
    expiresAt: Date.now() + MFA_TRUST_DURATION_MS
  };
  localStorage.setItem(getMfaTrustKey(userId), JSON.stringify(record));
}

function hasValidTrustedMfaDevice(userId: string): boolean {
  try {
    const trustKey = getMfaTrustKey(userId);
    const raw = localStorage.getItem(trustKey);
    if (!raw) return false;
    const record = JSON.parse(raw) as { expiresAt?: number };
    if (typeof record.expiresAt !== 'number') return false;
    if (Date.now() >= record.expiresAt) {
      // Remove expired trust records so stale devices do not keep passing MFA checks.
      localStorage.removeItem(trustKey);
      return false;
    }
    return true;
  } catch {
    localStorage.removeItem(getMfaTrustKey(userId));
    return false;
  }
}

export function subscribeToAuthState(listener: AuthStateListener): () => void {
  listeners.push(listener);

  if (USE_FIREBASE) {
    let unsubFirebase: (() => void) | null = null;
    let isActive = true;

    void initializeFirebaseAuth()
      .then((auth) => {
        if (!isActive) return;
        unsubFirebase = onAuthStateChanged(auth, async (fbUser) => {
          if (!isActive) return;
          console.info('[auth] onAuthStateChanged fired', {
            user: fbUser ? fbUser.uid : null,
          });
          // Convert Firebase's auth callback into the app-specific user model
          // before handing it to listeners.
          const user = fbUser ? await enrichUserWithRoles(fbUser) : null;
          listener(user);
        });
      })
      .catch((err) => {
        console.error('Failed to initialize Firebase auth state listener:', err);
        if (isActive) {
          listener(null);
        }
      });

    return () => {
      isActive = false;
      const index = listeners.indexOf(listener);
      if (index > -1) listeners.splice(index, 1);
      unsubFirebase?.();
    };
  }

  // Emit initial mock auth state on subscription so hooks can resolve loading
  // without requiring a separate getCurrentUser() call.
  void getCurrentUser()
    .then(user => listener(user))
    .catch(() => listener(null));

  return () => {
    const index = listeners.indexOf(listener);
    if (index > -1) listeners.splice(index, 1);
  };
}

/**
 * Custom error thrown when MFA verification is required during login.
 */
export class MfaRequiredError extends Error {
  code = 'auth/multi-factor-auth-required';
  resolver: any;
  pendingUser: User | null;

  constructor(resolver: any, pendingUser: User | null) {
    super('Multi-factor authentication required.');
    this.resolver = resolver;
    this.pendingUser = pendingUser;
  }
}

export async function login(email: string, password: string): Promise<User> {
  const normalizedEmail = normalizeEmail(email);
  if (USE_FIREBASE) {
    await initializeFirebaseAuth();
    try {
      // Delegate password validation to Firebase Auth, which returns the signed-in user on success.
      const cred = await signInWithEmailAndPassword(getFirebaseAuth(), normalizedEmail, password);
      const user = await enrichUserWithRoles(cred.user);
      notifyListeners(user);
      return user;
    } catch (err: any) {
      if (err?.code === 'auth/multi-factor-auth-required') {
        // Convert Firebase's MFA error into an app-specific error object so the UI
        // knows it should show the second-factor challenge instead of a generic failure.
        const resolver = getMultiFactorResolver(getFirebaseAuth(), err as MultiFactorError);
        throw new MfaRequiredError(resolver, null);
      }
      throw err;
    }
  }

  // Mock login
  await new Promise(resolve => setTimeout(resolve, 500));
  const key = normalizedEmail;
  const record = MOCK_USERS[key];

  const attempt = loginAttempts.get(key);
  if (attempt?.lockedUntil && Date.now() < attempt.lockedUntil) {
    // Stop early when the identifier is still locked so repeated retries do not bypass the delay.
    throw new Error('Too many login attempts. Try again later.');
  }

  if (!record || record.password !== password) {
    const next = (attempt?.count || 0) + 1;
    if (next >= MAX_ATTEMPTS) {
      // Lock the account after too many failures to slow brute-force guessing in local mode too.
      loginAttempts.set(key, { count: next, lockedUntil: Date.now() + LOCK_WINDOW_MS });
      throw new Error('Account temporarily locked after too many attempts.');
    }
    loginAttempts.set(key, { count: next });
    throw new Error('Invalid email or password');
  }

  loginAttempts.delete(key);

  // Check if MFA is enrolled in mock mode
  const isMfaEnrolledForUser =
    localStorage.getItem(getMfaEnrollmentKey(record.user.id)) === 'true' ||
    localStorage.getItem(LEGACY_MFA_ENROLLED_KEY) === 'true';
  if (isMfaEnrolledForUser && !hasValidTrustedMfaDevice(record.user.id)) {
    // Require MFA when the user has enrolled a factor but the current device is not trusted.
    throw new MfaRequiredError(null, record.user);
  }

  setSession(record.user);
  notifyListeners(record.user);
  return record.user;
}

/**
 * Complete login for a user who passed MFA verification (mock mode).
 */
export function completeMfaLogin(user: User): void {
  // Remember the device first so the follow-up login session can skip MFA until trust expires.
  rememberTrustedMfaDevice(user.id);
  setSession(user);
  notifyListeners(user);
}

export async function loginWithGoogle(): Promise<User> {
  if (!USE_FIREBASE) {
    throw new Error('Google Sign-In is only available with Firebase.');
  }
  await initializeFirebaseAuth();
  try {
    const provider = new GoogleAuthProvider();
    // Open Google's OAuth popup through Firebase Auth so Google returns a Firebase-authenticated user.
    const cred = await signInWithPopup(getFirebaseAuth(), provider);
    await saveUserProfileToFirestore(cred.user);
    const user = await enrichUserWithRoles(cred.user);
    notifyListeners(user);
    return user;
  } catch (err: any) {
    if (err?.code === 'auth/multi-factor-auth-required') {
      const resolver = getMultiFactorResolver(getFirebaseAuth(), err as MultiFactorError);
      throw new MfaRequiredError(resolver, null);
    }
    throw err;
  }
}

export async function register(input: { displayName: string; email: string; password: string; phone?: string; organization?: string; address?: string; organizationAddress?: string }): Promise<User> {
  const normalizedDisplayName = sanitizeSingleLineText(input.displayName, INPUT_LIMITS.displayName);
  const normalizedEmail = normalizeEmail(input.email);
  const normalizedPhone = input.phone ? normalizeUsPhoneE164(input.phone) : undefined;
  const normalizedOrganization = toOptionalSingleLineText(input.organization, INPUT_LIMITS.organization);
  const normalizedAddress = toOptionalSingleLineText(input.organizationAddress || input.address, INPUT_LIMITS.address);

  assert(Boolean(normalizedDisplayName), 'Display name is required.');
  assert(isValidEmail(normalizedEmail), 'Please enter a valid email address.');
  assert(isStrongPassword(input.password), 'Password must be 8+ chars and include at least one uppercase letter and one number.');
  assert(!input.phone || Boolean(normalizedPhone), 'Please enter a valid 10-digit phone number.');

  if (USE_FIREBASE) {
    await initializeFirebaseAuth();
    // Create the Firebase Auth account first because the uid it returns is needed for the profile document.
    const cred = await createUserWithEmailAndPassword(getFirebaseAuth(), normalizedEmail, input.password);
    await updateProfile(cred.user, { displayName: normalizedDisplayName });
    // Save profile fields to Firestore because the app reads organization and phone data from there later.
    await saveUserProfileToFirestore(cred.user, {
      phone: normalizedPhone,
      organization: normalizedOrganization,
      address: normalizedAddress,
      organizationAddress: normalizedAddress,
    });
    // Send email verification so the user can verify before enrolling MFA.
    // Uses ActionCodeSettings that do not rely on Firebase Dynamic Links.
    try {
      await sendEmailVerification(cred.user, getActionCodeSettings());
    } catch (err) {
      console.error('Failed to send email verification:', err);
    }
    const user = firebaseUserToUser({ ...cred.user, displayName: normalizedDisplayName });
    notifyListeners(user);
    return user;
  }

  // Mock register
  await new Promise(resolve => setTimeout(resolve, 500));
  const key = normalizedEmail;

  if (MOCK_USERS[key]) {
    throw new Error('An account with this email already exists');
  }

  if (input.password.length < 8 || !/[A-Z]/.test(input.password) || !/[0-9]/.test(input.password)) {
    throw new Error('Password must be 8+ chars and include at least one uppercase letter and one number.');
  }

  const user: User = {
    id: `user-${Date.now()}`,
    email: key,
    displayName: normalizedDisplayName,
    organization: normalizedOrganization || '',
    phone: normalizedPhone || '',
    organizationAddress: normalizedAddress || '',
    address: normalizedAddress || '',
    roles: ['user'],
    assignedBuildings: ['building-001']
  };

  MOCK_USERS = {
    ...MOCK_USERS,
    [key]: { password: input.password, user }
  };

  setSession(user);
  notifyListeners(user);
  return user;
}

export async function requestPasswordReset(email: string): Promise<void> {
  const normalizedEmail = normalizeEmail(email);
  assert(isValidEmail(normalizedEmail), 'Please enter a valid email address.');
  if (USE_FIREBASE) {
    await sendPasswordResetEmail(getFirebaseAuth(), normalizedEmail, getActionCodeSettings());
    return;
  }
  await new Promise(resolve => setTimeout(resolve, 400));
  void normalizedEmail;
}

export async function updateCurrentUserProfile(input: { displayName: string }): Promise<User | null> {
  const normalizedDisplayName = sanitizeSingleLineText(input.displayName, INPUT_LIMITS.displayName);
  assert(Boolean(normalizedDisplayName), 'Display name is required.');
  if (USE_FIREBASE) {
    const fbUser = getFirebaseAuth().currentUser;
    if (fbUser) {
      await updateProfile(fbUser, { displayName: normalizedDisplayName });
      const user = firebaseUserToUser({ ...fbUser, displayName: normalizedDisplayName });
      notifyListeners(user);
      return user;
    }
  }

  const current = await getCurrentUser();
  if (!current) return null;

  const updated: User = { ...current, displayName: normalizedDisplayName };

  const key = updated.email.toLowerCase();
  if (MOCK_USERS[key]) {
    MOCK_USERS[key] = { ...MOCK_USERS[key], user: updated };
  }

  setSession(updated);
  notifyListeners(updated);
  return updated;
}

export async function updateCurrentUserDetails(input: {
  displayName?: string;
  phone?: string;
  organization?: string;
  organizationAddress?: string;
}): Promise<User | null> {
  const displayName = input.displayName ? sanitizeSingleLineText(input.displayName, INPUT_LIMITS.displayName) : undefined;
  const normalizedPhone = input.phone ? normalizeUsPhoneE164(input.phone) : undefined;
  const normalizedOrganization = toOptionalSingleLineText(input.organization, INPUT_LIMITS.organization);
  const normalizedAddress = toOptionalSingleLineText(input.organizationAddress, INPUT_LIMITS.address);

  assert(!input.displayName || Boolean(displayName), 'Display name is required.');
  assert(!input.phone || Boolean(normalizedPhone), 'Please enter a valid 10-digit phone number.');

  if (USE_FIREBASE) {
    const fbUser = getFirebaseAuth().currentUser;
    if (!fbUser) return null;
    if (displayName) {
      await updateProfile(fbUser, { displayName });
    }
    await saveUserProfileToFirestore(fbUser, {
      phone: normalizedPhone,
      organization: normalizedOrganization,
      organizationAddress: normalizedAddress,
      address: normalizedAddress,
    });
    const user = await enrichUserWithRoles(fbUser);
    notifyListeners(user);
    return user;
  }

  const current = await getCurrentUser();
  if (!current) return null;
  const updated: User = {
    ...current,
    displayName: displayName || current.displayName,
    phone: normalizedPhone ?? current.phone ?? '',
    organization: normalizedOrganization ?? current.organization ?? '',
    organizationAddress: normalizedAddress ?? current.organizationAddress ?? '',
    address: normalizedAddress ?? current.address ?? '',
  };
  const key = updated.email.toLowerCase();
  if (MOCK_USERS[key]) {
    MOCK_USERS[key] = { ...MOCK_USERS[key], user: updated };
  }
  setSession(updated);
  notifyListeners(updated);
  return updated;
}

export async function logout(): Promise<void> {
  if (USE_FIREBASE) {
    await signOut(getFirebaseAuth());
    notifyListeners(null);
    return;
  }
  await new Promise(resolve => setTimeout(resolve, 250));
  localStorage.removeItem('mockAuthSession');
  notifyListeners(null);
}

export async function getCurrentUser(): Promise<User | null> {
  if (USE_FIREBASE) {
    const fbUser = getFirebaseAuth().currentUser;
    return fbUser ? enrichUserWithRoles(fbUser) : null;
  }

  const sessionJson = localStorage.getItem('mockAuthSession');
  if (!sessionJson) return null;

  try {
    const session: AuthSession = JSON.parse(sessionJson);
    if (Date.now() > session.expiresAt) {
      localStorage.removeItem('mockAuthSession');
      return null;
    }
    return session.user;
  } catch {
    return null;
  }
}

export async function isAuthenticated(): Promise<boolean> {
  return (await getCurrentUser()) !== null;
}

export async function hasRole(role: string): Promise<boolean> {
  const user = await getCurrentUser();
  return user?.roles.includes(role as any) || false;
}

export function requiresProfileCompletion(user: User | null): boolean {
  if (!user) return false;
  return !(
    user.displayName?.trim() &&
    user.phone?.trim() &&
    user.organization?.trim() &&
    (user.organizationAddress?.trim() || user.address?.trim())
  );
}

/**
 * Check whether the current Firebase user's email has been verified.
 * Returns true in mock mode since no real verification is needed.
 */
export function isEmailVerified(): boolean {
  if (!USE_FIREBASE) return true;
  const fbUser = getFirebaseAuth().currentUser;
  return fbUser?.emailVerified ?? false;
}

/**
 * Re-send the email verification link for the current user.
 * Uses ActionCodeSettings that do not rely on Firebase Dynamic Links.
 */
export async function resendEmailVerification(): Promise<void> {
  if (!USE_FIREBASE) return;
  const fbUser = getFirebaseAuth().currentUser;
  if (!fbUser) throw new Error('No authenticated user');
  if (fbUser.emailVerified) return;
  await sendEmailVerification(fbUser, getActionCodeSettings());
}

/**
 * Sign in using a Firebase custom authentication token.
 * Used for Firebase Phone Number Verification (PNV) token exchange flow
 * where a backend endpoint validates the PNV token and returns a custom token.
 */
export async function signInWithCustomToken(customToken: string): Promise<User> {
  if (!USE_FIREBASE) {
    throw new Error('Custom token sign-in is only available with Firebase.');
  }
  await initializeFirebaseAuth();
  const cred = await firebaseSignInWithCustomToken(getFirebaseAuth(), customToken);
  await saveUserProfileToFirestore(cred.user);
  const user = await enrichUserWithRoles(cred.user);
  notifyListeners(user);
  return user;
}

export async function refreshSession(): Promise<void> {
  if (USE_FIREBASE) return;
  const sessionJson = localStorage.getItem('mockAuthSession');
  if (!sessionJson) return;
  const session: AuthSession = JSON.parse(sessionJson);
  session.expiresAt = Date.now() + 3600000;
  localStorage.setItem('mockAuthSession', JSON.stringify(session));
}

/**
 * Session inactivity timeout.
 * Logs the user out after 4 hours of inactivity by default.
 * After logout, the next sign-in will require MFA verification again.
 */
const DEFAULT_INACTIVITY_TIMEOUT_MS = 4 * 60 * 60 * 1000; // 4 hours default
const INACTIVITY_TIMEOUT_MS = Number(process.env.REACT_APP_SESSION_TIMEOUT_MS || DEFAULT_INACTIVITY_TIMEOUT_MS);
let inactivityTimer: ReturnType<typeof setTimeout> | null = null;

function resetInactivityTimer() {
  if (inactivityTimer) clearTimeout(inactivityTimer);
  inactivityTimer = setTimeout(async () => {
    const user = await getCurrentUser();
    if (user) {
      await logout();
      window.dispatchEvent(new CustomEvent('session-timeout'));
    }
  }, INACTIVITY_TIMEOUT_MS);
}

export function startSessionMonitor() {
  // For Firebase Auth, rely on Firebase token/session persistence instead of
  // forcing a client-side inactivity sign-out that can trigger repeated MFA.
  if (USE_FIREBASE) return;
  const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
  events.forEach(evt => window.addEventListener(evt, resetInactivityTimer, { passive: true }));
  resetInactivityTimer();
}

export function stopSessionMonitor() {
  if (inactivityTimer) clearTimeout(inactivityTimer);
  const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
  events.forEach(evt => window.removeEventListener(evt, resetInactivityTimer));
}
